import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, Beaker, Shield, Zap, Heart, Brain, Activity, ArrowRight, ExternalLink, FlaskConical, Flame, Dna } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const ARTICLE_SLUG = 'retatrutide-cancer-prevention-science';

const NAV = [
  { id: 'why', label: 'The Problem' },
  { id: 'failures', label: '3 Failures' },
  { id: 'mechanism', label: 'Triple-Agonist' },
  { id: 'data', label: 'Tumor Data' },
  { id: 'liver', label: 'Liver Data' },
  { id: 'immune', label: 'Immune System' },
  { id: 'faq', label: 'FAQ' },
];

const COMPARISON_DATA = [
  { name: 'Retatrutide', glp1: true, gip: true, glucagon: true, tumorReduction: '14x', weightLoss: '24.2%', liverFat: '82.4%', color: '#E63946' },
  { name: 'Tirzepatide', glp1: true, gip: true, glucagon: false, tumorReduction: '~6x', weightLoss: '22.5%', liverFat: '55%', color: '#f59e0b' },
  { name: 'Semaglutide', glp1: true, gip: false, glucagon: false, tumorReduction: '4x', weightLoss: '16.9%', liverFat: '42%', color: '#6b7280' },
];

const FAILURES = [
  {
    icon: Flame,
    title: 'Chronic Systemic Inflammation',
    color: '#ef4444',
    markers: ['TNF-alpha', 'IL-6', 'IL-1 beta'],
    detail: 'Your immune system stays in overdrive permanently, releasing pro-inflammatory cytokines around the clock. This creates a tumor-promoting microenvironment where cancer cells don\'t just survive — they flourish.',
  },
  {
    icon: Activity,
    title: 'Insulin Resistance & Metabolic Dysfunction',
    color: '#f59e0b',
    markers: ['Elevated Insulin', 'IGF-1', 'HOMA-IR'],
    detail: 'Elevated insulin directly stimulates cell proliferation. It activates IGF-1 pathways, telling cells to grow, divide, and multiply. Type 2 diabetes increases susceptibility to 8+ cancer types.',
  },
  {
    icon: Dna,
    title: 'ATP Shortage & Mitochondrial Dysfunction',
    color: '#8b5cf6',
    markers: ['Warburg Effect', 'Anaerobic Glycolysis', 'mtDNA Damage'],
    detail: 'When mitochondria are damaged, cells switch to anaerobic glycolysis — the Warburg Effect. This isn\'t a consequence of cancer. It\'s a precondition. Damaged mitochondria come first.',
  },
];

const TUMOR_DATA = [
  { cancer: 'Pancreatic', retatrutide: 14, semaglutide: 4, color: '#E63946' },
  { cancer: 'Lung', retatrutide: 17, semaglutide: 3, color: '#3b82f6' },
];

const FAQS = [
  { q: 'What is retatrutide?', a: 'A triple-agonist peptide (GLP-1/GIP/Glucagon) by Eli Lilly that produced 14x more tumor reduction than semaglutide in preclinical models.' },
  { q: 'Can retatrutide prevent cancer?', a: 'Preclinical research shows significant tumor reduction and persistent anti-tumor immunity. Human cancer prevention trials have not yet been conducted.' },
  { q: 'What biomarkers should I track?', a: 'Liver fat %, fasting insulin, HOMA-IR, hs-CRP, HbA1c, and visceral fat via DEXA scan.' },
  { q: 'How does it compare to semaglutide?', a: 'Retatrutide activates 3 receptors vs. semaglutide\'s 1. This produces 14x more tumor reduction, 82% liver fat reduction (vs ~42%), and 24% body weight reduction (vs ~17%).' },
  { q: 'Is retatrutide FDA-approved?', a: 'Not yet. It is currently in Phase 3 clinical trials for obesity. The cancer prevention data comes from preclinical (animal) research published in Nature-affiliated journals.' },
];

const LIVER_STATS = [
  { label: 'Liver Fat Reduction', value: '82.4%', sub: '24 weeks' },
  { label: 'Normal Liver Fat Achieved', value: '86%', sub: 'of participants' },
  { label: 'Body Weight Reduction', value: '24.2%', sub: 'mean' },
  { label: 'Visceral Fat Reduction', value: '48%', sub: 'up to' },
];

export default function RetatrutideArticle() {
  const [activeNav, setActiveNav] = useState('why');
  const [openFaq, setOpenFaq] = useState(null);
  const [openFailure, setOpenFailure] = useState(0);
  const [showComparison, setShowComparison] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.round((scrollTop / docHeight) * 100)) : 0;
      setProgressForSlug(ARTICLE_SLUG, pct);

      const sections = NAV.map(n => document.getElementById(n.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 150) {
          setActiveNav(NAV[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={containerRef} className="min-h-screen bg-[#0a0a0a] text-zinc-300">
      {/* Sticky Nav */}
      <div className="sticky top-[60px] z-40 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {NAV.map(n => (
            <button key={n.id} onClick={() => document.getElementById(n.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className={`px-3 py-1.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap transition-all ${activeNav === n.id ? 'bg-[#E63946] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>{n.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8 pb-20">
        {/* Hero */}
        <div className="mb-12">
          <span className="text-[#E63946] text-[10px] uppercase tracking-widest font-heading">Peptides / Cancer Research</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mt-3 mb-4 leading-tight">
            Retatrutide & Cancer Prevention
          </h1>
          <p className="text-zinc-400 text-base max-w-xl">The science behind the triple-agonist peptide that produced a 14-fold tumor volume reduction in preclinical models.</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-zinc-600 font-mono">
            <span>22 min read</span>
            <span>Marathe et al., 2025</span>
          </div>
        </div>

        {/* Callout */}
        <div className="bg-[#E63946]/5 border border-[#E63946]/20 p-5 mb-10" data-testid="hero-callout">
          <div className="flex gap-3">
            <FlaskConical className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-bold mb-1">Key Finding</p>
              <p className="text-zinc-400 text-sm leading-relaxed">Retatrutide produced a <strong className="text-white">14-fold tumor volume reduction</strong> vs. semaglutide's 4-fold in preclinical models. The protection persisted even after stopping the drug.</p>
            </div>
          </div>
        </div>

        {/* Section: Why */}
        <section id="why" className="mb-16 scroll-mt-28" data-testid="section-why">
          <h2 className="font-heading text-2xl text-white mb-4">Why Conventional Medicine Gets Cancer Prevention Wrong</h2>
          <p className="text-zinc-400 leading-relaxed mb-4">You go to your doctor for your annual physical. They check your blood pressure, maybe run some basic bloodwork, tell you to eat more vegetables, and send you home. Meanwhile, your liver is drowning in fat. Your insulin levels are through the roof. Your mitochondria are limping along at 40% capacity.</p>
          <blockquote className="border-l-2 border-[#E63946] pl-4 py-2 my-6 text-zinc-500 italic text-sm">
            "Over 40% of the adult population in the U.S. is obese. Obesity is associated with an increased risk of at least <strong className="text-zinc-300">thirteen cancers</strong>." — Marathe et al., NPJ Metabolic Health and Disease, 2025
          </blockquote>
          <p className="text-zinc-400 leading-relaxed">The medical establishment is failing at cancer prevention. Not because they don't care, but because they're looking at cancer through the wrong lens. <strong className="text-white">Cancer, in the vast majority of cases, is a metabolic disease.</strong></p>
        </section>

        {/* Section: 3 Failures */}
        <section id="failures" className="mb-16 scroll-mt-28" data-testid="section-failures">
          <h2 className="font-heading text-2xl text-white mb-6">The Three Biological Failures That Feed Cancer</h2>
          <div className="space-y-3">
            {FAILURES.map((f, i) => {
              const Icon = f.icon;
              const isOpen = openFailure === i;
              return (
                <div key={f.title} className={`border transition-all cursor-pointer ${isOpen ? 'border-zinc-700 bg-zinc-900/80' : 'border-zinc-800/50 bg-zinc-900/30 hover:border-zinc-700'}`}
                  onClick={() => setOpenFailure(isOpen ? -1 : i)} data-testid={`failure-${i}`}>
                  <div className="flex items-center gap-3 p-4">
                    <span className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${f.color}15`, color: f.color }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1">
                      <p className="text-white text-sm font-bold">Failure #{i + 1}: {f.title}</p>
                      <div className="flex gap-2 mt-1">
                        {f.markers.map(m => <span key={m} className="text-[9px] font-mono px-1.5 py-0.5 bg-zinc-800 text-zinc-500">{m}</span>)}
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-0">
                      <p className="text-zinc-400 text-sm leading-relaxed pl-11">{f.detail}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section: Triple-Agonist */}
        <section id="mechanism" className="mb-16 scroll-mt-28" data-testid="section-mechanism">
          <h2 className="font-heading text-2xl text-white mb-4">Retatrutide: The Triple-Agonist Mechanism</h2>
          <p className="text-zinc-400 leading-relaxed mb-6">Unlike semaglutide (GLP-1 only) or tirzepatide (GLP-1 + GIP), <strong className="text-white">retatrutide activates three receptors simultaneously:</strong></p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {[
              { receptor: 'GLP-1', icon: Heart, effects: ['Glucose control', 'Appetite regulation', 'Immune modulation'], color: '#E63946' },
              { receptor: 'GIP', icon: Shield, effects: ['Fat redistribution', 'Insulin sensitization', 'Tissue repair'], color: '#f59e0b' },
              { receptor: 'Glucagon', icon: Zap, effects: ['Energy expenditure +25%', 'Liver fat oxidation', 'Thermogenesis'], color: '#8b5cf6' },
            ].map(r => {
              const RIcon = r.icon;
              return (
                <div key={r.receptor} className="bg-zinc-900/80 border border-zinc-800 p-4" data-testid={`receptor-${r.receptor.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${r.color}15`, color: r.color }}>
                      <RIcon className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-white text-sm font-bold font-heading">{r.receptor}</span>
                  </div>
                  <ul className="space-y-1">
                    {r.effects.map(e => <li key={e} className="text-zinc-500 text-xs flex items-start gap-1.5"><span style={{ color: r.color }} className="mt-1">-</span> {e}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Comparison toggle */}
          <button onClick={() => setShowComparison(!showComparison)}
            className="text-xs font-heading uppercase tracking-wider text-[#E63946] hover:text-white transition-colors flex items-center gap-2"
            data-testid="toggle-comparison">
            {showComparison ? 'Hide' : 'Show'} Full Comparison Table
            <ChevronDown className={`w-3 h-3 transition-transform ${showComparison ? 'rotate-180' : ''}`} />
          </button>

          {showComparison && (
            <div className="mt-4 overflow-x-auto" data-testid="comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-zinc-600 text-[10px] uppercase tracking-wider py-2 pr-4">Drug</th>
                    <th className="text-center text-zinc-600 text-[10px] uppercase py-2 px-2">GLP-1</th>
                    <th className="text-center text-zinc-600 text-[10px] uppercase py-2 px-2">GIP</th>
                    <th className="text-center text-zinc-600 text-[10px] uppercase py-2 px-2">Glucagon</th>
                    <th className="text-right text-zinc-600 text-[10px] uppercase py-2 px-2">Tumor Red.</th>
                    <th className="text-right text-zinc-600 text-[10px] uppercase py-2 px-2">Weight</th>
                    <th className="text-right text-zinc-600 text-[10px] uppercase py-2 pl-2">Liver Fat</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_DATA.map(d => (
                    <tr key={d.name} className="border-b border-zinc-800/50">
                      <td className="py-2.5 pr-4 font-bold" style={{ color: d.color }}>{d.name}</td>
                      <td className="text-center py-2.5 px-2">{d.glp1 ? <span className="text-green-500">Y</span> : <span className="text-zinc-700">-</span>}</td>
                      <td className="text-center py-2.5 px-2">{d.gip ? <span className="text-green-500">Y</span> : <span className="text-zinc-700">-</span>}</td>
                      <td className="text-center py-2.5 px-2">{d.glucagon ? <span className="text-green-500">Y</span> : <span className="text-zinc-700">-</span>}</td>
                      <td className="text-right py-2.5 px-2 text-white font-mono">{d.tumorReduction}</td>
                      <td className="text-right py-2.5 px-2 text-white font-mono">{d.weightLoss}</td>
                      <td className="text-right py-2.5 pl-2 text-white font-mono">{d.liverFat}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Section: Tumor Data */}
        <section id="data" className="mb-16 scroll-mt-28" data-testid="section-data">
          <h2 className="font-heading text-2xl text-white mb-2">The Tumor Reduction Data</h2>
          <p className="text-zinc-600 text-xs font-mono mb-6">Marathe et al., NPJ Metabolic Health and Disease, 2025</p>
          <div className="space-y-6">
            {TUMOR_DATA.map(t => (
              <div key={t.cancer} className="bg-zinc-900/50 border border-zinc-800 p-5" data-testid={`tumor-${t.cancer.toLowerCase()}`}>
                <p className="text-white font-heading text-sm mb-4">{t.cancer} Cancer</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-[#E63946] font-bold">Retatrutide</span>
                      <span className="text-white font-mono">{t.retatrutide}x reduction</span>
                    </div>
                    <div className="h-3 bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-[#E63946] transition-all duration-1000" style={{ width: `${(t.retatrutide / 20) * 100}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-500">Semaglutide</span>
                      <span className="text-zinc-400 font-mono">{t.semaglutide}x reduction</span>
                    </div>
                    <div className="h-3 bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-zinc-600 transition-all duration-1000" style={{ width: `${(t.semaglutide / 20) * 100}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-zinc-900/30 border border-zinc-800/50 p-4">
            <p className="text-zinc-500 text-xs"><strong className="text-zinc-300">Durability:</strong> Anti-tumor benefits persisted even after drug withdrawal. <strong className="text-zinc-300">Immune Reprogramming:</strong> Elevated IL-6, increased antigen-presenting cells.</p>
          </div>
        </section>

        {/* Section: Liver */}
        <section id="liver" className="mb-16 scroll-mt-28" data-testid="section-liver">
          <h2 className="font-heading text-2xl text-white mb-2">Your Liver Is the Cancer Prevention Lynchpin</h2>
          <p className="text-zinc-600 text-xs font-mono mb-6">Sanyal et al., Nature Medicine 2024 — Phase 2 MASLD Trial</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LIVER_STATS.map(s => (
              <div key={s.label} className="bg-zinc-900/60 border border-zinc-800 p-4 text-center" data-testid={`liver-stat-${s.label.slice(0,10).toLowerCase().replace(/\s/g,'-')}`}>
                <p className="text-[#E63946] text-2xl font-heading font-bold">{s.value}</p>
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider mt-1">{s.sub}</p>
                <p className="text-zinc-400 text-xs mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section: Immune */}
        <section id="immune" className="mb-16 scroll-mt-28" data-testid="section-immune">
          <h2 className="font-heading text-2xl text-white mb-4">Immune Reprogramming: M1/M2 Macrophage Polarization</h2>
          <p className="text-zinc-400 leading-relaxed mb-4">GLP-1 receptor activation drives macrophage polarization from pro-inflammatory M1 to anti-inflammatory M2 through STAT3 activation and NF-kB inhibition. This shifts the body from a tumor-promoting to an anti-tumor surveillance state.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-red-950/20 border border-red-900/20 p-4">
              <p className="text-red-400 text-xs font-heading uppercase tracking-wider mb-2">Before (M1 Dominant)</p>
              <ul className="text-zinc-400 text-sm space-y-1">
                <li>- Pro-inflammatory cytokine storm</li>
                <li>- Tumor-promoting microenvironment</li>
                <li>- Chronic NF-kB activation</li>
              </ul>
            </div>
            <div className="bg-green-950/20 border border-green-900/20 p-4">
              <p className="text-green-400 text-xs font-heading uppercase tracking-wider mb-2">After (M2 Dominant)</p>
              <ul className="text-zinc-400 text-sm space-y-1">
                <li>- Anti-tumor surveillance active</li>
                <li>- STAT3-mediated immune balance</li>
                <li>- Elevated antigen-presenting cells</li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-[#E63946]/5 border border-[#E63946]/20 p-6 mb-12" data-testid="cta-block">
          <p className="text-[10px] text-[#E63946] font-heading uppercase tracking-widest mb-2">Research-Grade Retatrutide</p>
          <p className="text-white font-heading text-lg mb-1">Third-party tested. Purity verified.</p>
          <p className="text-zinc-500 text-sm mb-4">Trusted by researchers nationwide.</p>
          <a href="https://www.americanpeptideresearch.net/product/reta-30mg/ref/16/" target="_blank" rel="sponsored noopener"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E63946] text-white text-xs font-heading uppercase tracking-wider hover:bg-[#c41730] transition-colors"
            data-testid="cta-apr">
            View Retatrutide at APR <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* FAQ */}
        <section id="faq" className="mb-16 scroll-mt-28" data-testid="section-faq">
          <h2 className="font-heading text-2xl text-white mb-6">Frequently Asked Questions</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={f.q} className={`border transition-all ${openFaq === i ? 'border-zinc-700 bg-zinc-900/60' : 'border-zinc-800/50'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left" data-testid={`faq-${i}`}>
                  <span className="text-white text-sm font-bold pr-4">{f.q}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                </button>
                {openFaq === i && <p className="px-4 pb-4 text-zinc-400 text-sm leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        {/* Disclaimer */}
        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Disclaimer:</strong> This article is for educational purposes only. Retatrutide is currently in clinical trials and is not FDA-approved for cancer prevention. The tumor reduction data comes from preclinical (animal) research. Consult a healthcare provider before making any protocol changes.
        </div>
      </div>
    </div>
  );
}
